<template>
  <div class="mt-[15px] rounded-md border bg-gray-25 p-4">
    <div v-if="checking" class="flex h-[100px] items-center justify-center">
      <UISpin />
    </div>

    <!-- Voice has more prerequisites than any other channel; list what is missing rather than
         render a form that can never place a call. -->
    <div v-else-if="blockers.length" id="voice-ai-setup" class="space-y-2 py-2">
      <div class="text-[16px] font-semibold text-gray-900">
        {{ t('eventNotification.voiceAi.setupTitle') }}
      </div>
      <div class="text-[13px] text-gray-500">
        {{ t('eventNotification.voiceAi.setupDescription') }}
      </div>
      <ul class="ml-4 list-disc text-[14px] text-gray-700">
        <li v-for="blocker in blockers" :key="blocker">{{ blockerLabel(blocker) }}</li>
      </ul>
    </div>

    <template v-else>
      <div class="mb-5 flex items-center space-x-3">
        <UISwitch
          id="voice-ai-notification-switch"
          v-model:value="enabled"
          size="medium"
          :aria-label="t('alt_text.voice_ai_notification_switch')"
        />
        <span class="text-gray-600">{{ enabled ? t('eventNotification.enabled') : t('eventNotification.disabled') }}</span>
      </div>

      <UIForm :model="form" class="w-full space-y-4" :class="{ 'disabled-div': !enabled }">
        <UIFormItem :label="t('eventNotification.voiceAi.agentLabel')" :feedback="t('eventNotification.voiceAi.agentHelper')">
          <div class="flex w-full items-center gap-2">
            <UISelect
              id="voice-ai-agent-select"
              v-model:value="form.agentId"
              class="min-w-0 grow"
              :options="agentOptions"
              :placeholder="t('eventNotification.voiceAi.agentPlaceholder')"
              :filterable="true"
            />
            <UITooltip v-if="form.agentId" placement="top">
              <template #trigger>
                <UIButton
                  id="voice-ai-open-agent"
                  class="shrink-0"
                  :aria-label="t('eventNotification.voiceAi.openAgent')"
                  @click="openSelectedAgent"
                >
                  <LinkExternal01Icon class="h-4 w-4" />
                </UIButton>
              </template>
              {{ t('eventNotification.voiceAi.openAgent') }}
            </UITooltip>
          </div>
        </UIFormItem>

        <UIFormItem :label="t('eventNotification.voiceAi.fromNumberLabel')" :show-feedback="false">
          <UISelect
            id="voice-ai-from-number-select"
            v-model:value="form.fromNumber"
            :options="numberOptions"
            :placeholder="t('eventNotification.voiceAi.fromNumberPlaceholder')"
            :filterable="true"
          />
        </UIFormItem>

        <UIFormItem
          :label="t('eventNotification.voiceAi.timingLabel')"
          :feedback="offsetErrorMessage || timingHelper"
          :validation-status="offsetErrorMessage ? 'error' : undefined"
        >
          <div class="flex w-full items-center gap-3">
            <UIInputGroup class="w-64 shrink-0">
              <UIInput
                id="voice-ai-offset-value"
                class="w-28"
                inputmode="numeric"
                :model-value="String(form.interval.timeOffset ?? '')"
                @update:model-value="setOffsetValue"
              />
              <UISelect id="voice-ai-offset-unit" v-model:value="form.interval.unit" class="w-36" :options="unitOptions" />
            </UIInputGroup>
            <span class="whitespace-nowrap text-[14px] text-gray-600">{{ t('eventNotification.voiceAi.beforeAppointment') }}</span>
          </div>
        </UIFormItem>
      </UIForm>

      <div
        v-if="consentState !== 'accepted'"
        id="voice-ai-consent"
        class="mt-5 rounded-md border border-gray-200 bg-white px-4 py-3"
        :class="{ 'disabled-div': !enabled }"
      >
        <UICheckbox id="voice-ai-terms-checkbox" v-model:checked="form.termsAgreed">
          {{
            consentState === 'upgrade' ? t('eventNotification.voiceAi.consent.updatedLabel') : t('eventNotification.voiceAi.consent.label')
          }}
        </UICheckbox>
        <div class="ml-7 text-[12px] text-gray-500">
          {{
            consentState === 'upgrade' ? t('eventNotification.voiceAi.consent.updatedNote') : t('eventNotification.voiceAi.consent.note')
          }}
        </div>
        <UICollapse class="mt-2" :arrow-placement="'left'">
          <UICollapseItem id="voice-ai-terms" name="terms" :title="t('eventNotification.voiceAi.consent.termsTitle')">
            <p class="text-[13px] font-medium text-gray-800">{{ t('eventNotification.voiceAi.consent.termsIntro') }}</p>
            <ul class="mt-1 list-disc space-y-1 pl-5 text-[13px] text-gray-700">
              <li>{{ t('eventNotification.voiceAi.consent.terms1') }}</li>
              <li>{{ t('eventNotification.voiceAi.consent.terms2') }}</li>
              <li>{{ t('eventNotification.voiceAi.consent.terms3') }}</li>
            </ul>
          </UICollapseItem>
        </UICollapse>
      </div>

      <div
        v-if="!fallbackChannel"
        id="voice-ai-no-fallback"
        class="mt-5 flex items-start gap-2 rounded-md border border-warning-200 bg-warning-25 px-3 py-2 text-[13px] text-warning-700"
      >
        <AlertTriangleIcon class="mt-[2px] h-4 w-4 shrink-0" />
        <span>{{ t('eventNotification.voiceAi.guidelines.noFallback') }}</span>
      </div>

      <div id="voice-ai-guidelines" class="mt-5 rounded-md border border-gray-200 bg-white">
        <button
          type="button"
          class="flex w-full items-center gap-2 px-4 py-3 text-left"
          :aria-expanded="guidelinesOpen"
          @click="guidelinesOpen = !guidelinesOpen"
        >
          <InfoCircleIcon class="h-4 w-4 shrink-0 text-gray-500" />
          <span class="grow text-[13px] font-semibold text-gray-800">{{ t('eventNotification.voiceAi.guidelines.title') }}</span>
          <ChevronDownIcon class="h-4 w-4 shrink-0 text-gray-500 transition-transform" :class="{ 'rotate-180': guidelinesOpen }" />
        </button>
        <ul v-if="guidelinesOpen" class="list-disc space-y-1.5 border-t border-gray-100 py-3 pl-9 pr-4 text-[13px] text-gray-700">
          <li>{{ t('eventNotification.voiceAi.guidelines.window') }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.oncePerDay') }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.queued') }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.skips') }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.country') }}</li>
          <li v-if="fallbackChannel">{{ t('eventNotification.voiceAi.guidelines.fallback', { channel: fallbackChannel }) }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.capabilities') }}</li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { EventNotificationService } from '@/class/services/EventNotificationService'
  import config from '@/config'
  import { VoiceAiReminderService } from '@/class/services/VoiceAiReminderService'
  import AppState from '@/states/app'
  import {
    eventNotificationState as state,
    getEventNotificationState,
    isVirtualNotification,
    stripNotificationIdForCreate,
  } from '@/states/EventNotificationState'
  import { AltType, NotificationChannel, NotificationConfig, NotificationType, ReceiverType, Schedule } from '@/types/notification'
  import {
    DEFAULT_VOICE_REMINDER_OFFSET,
    getCallingWindowAdvice,
    getVoiceAgentUrl,
    getVoiceReminderOffsetError,
    getVoiceReminderUnits,
    VoiceReminderUnit,
  } from '@/utils/voiceAiReminder'
  import {
    UIButton,
    UICheckbox,
    UICollapse,
    UICollapseItem,
    UIForm,
    UIFormItem,
    UIInput,
    UIInputGroup,
    UISelect,
    UISpin,
    UISwitch,
    UITooltip,
  } from '@gohighlevel/ghl-ui'
  import { AlertTriangleIcon, ChevronDownIcon, InfoCircleIcon, LinkExternal01Icon } from '@gohighlevel/ghl-icons/24/outline'
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  type Blocker = 'no-agent' | 'no-number' | 'not-enabled'
  type ConsentState = 'accepted' | 'required' | 'upgrade'

  const props = defineProps({
    calendarId: { type: String, required: true },
  })

  const emit = defineEmits(['update-notification'])
  const { t } = useI18n()

  const checking = ref(true)
  const blockers = ref<Blocker[]>([])
  const agentOptions = ref<{ label: string; value: string; disabled?: boolean }[]>([])
  const numberOptions = ref<{ label: string; value: string }[]>([])
  const consentState = ref<ConsentState>('required')
  const enabled = ref(false)
  const rowId = ref('')
  const guidelinesOpen = ref(false)

  const form = reactive({
    agentId: '',
    fromNumber: '',
    termsAgreed: false,
    interval: { ...DEFAULT_VOICE_REMINDER_OFFSET } as Schedule,
  })

  const unitLabel = (unit: VoiceReminderUnit): string => {
    switch (unit) {
      case 'minutes':
        return t('eventNotification.voiceAi.units.minutes')
      case 'days':
        return t('eventNotification.voiceAi.units.days')
      default:
        return t('eventNotification.voiceAi.units.hours')
    }
  }

  const unitOptions = computed(() => getVoiceReminderUnits(config.mode).map(unit => ({ label: unitLabel(unit), value: unit })))

  const offsetErrorMessage = computed(() => {
    const error = getVoiceReminderOffsetError(form.interval, config.mode)
    if (!error) return ''
    return t('eventNotification.voiceAi.timingRangeError', {
      min: error.min,
      max: error.max,
      unit: unitLabel(error.unit).toLowerCase(),
    })
  })

  function setOffsetValue(value: string) {
    const digits = value.replace(/\D/g, '')

    form.interval.timeOffset = digits === '' ? ('' as unknown as number) : Number(digits)
  }

  const timingHelper = computed(() => {
    const advice = getCallingWindowAdvice(form.interval)
    return t('eventNotification.voiceAi.timingHelper', { from: advice.safeFromLabel, until: advice.safeUntilLabel })
  })

  // When the call can't be placed, the reminder falls back to a channel the admin already enabled.
  const fallbackChannel = computed(() => {
    if (AppState.isSmsNotificationsEnabled && getEventNotificationState('sms')) return t('eventNotification.sms.channelName')
    if (getEventNotificationState('email')) return t('eventNotification.email.channelName')
    if (getEventNotificationState('inApp')) return t('eventNotification.inApp.channelName')
    return ''
  })

  const blockerLabel = (blocker: Blocker): string => {
    switch (blocker) {
      case 'no-agent':
        return t('eventNotification.voiceAi.blockers.noAgent')
      case 'no-number':
        return t('eventNotification.voiceAi.blockers.noNumber')
      default:
        return t('eventNotification.voiceAi.blockers.notEnabled')
    }
  }

  function openSelectedAgent() {
    if (!form.agentId) return

    window.open(getVoiceAgentUrl(AppState.locationId, form.agentId), '_blank')?.focus()
  }

  /** A call we cannot confirm is a call we should not offer, so any failure reads as not ready. */
  async function checkReadiness() {
    checking.value = true
    const missing: Blocker[] = []
    try {
      const [agents, consent, numbers] = await Promise.all([
        VoiceAiReminderService.getAgents(AppState.locationId),
        VoiceAiReminderService.getOutboundConsent(AppState.locationId),
        VoiceAiReminderService.getLocationNumbers(AppState.locationId),
      ])
      agentOptions.value = (agents.data || [])
        .filter(agent => !agent.isDeleted)
        .map(agent => ({
          label: agent.name,
          value: agent.id,
          // An agent that has never been saved cannot place a call yet.
          disabled: agent.agentStatus === 'PENDING',
        }))
      numberOptions.value = (numbers.data?.numbers || []).map(number => ({
        label: number.friendlyName ? `${number.phoneNumber} — ${number.friendlyName}` : number.phoneNumber,
        value: number.phoneNumber,
      }))
      if (!agentOptions.value.some(option => !option.disabled)) missing.push('no-agent')
      if (!numberOptions.value.length) missing.push('no-number')
      if (!consent.data?.consentGiven) consentState.value = 'required'
      else if (!consent.data.selfCertifiedConsentGiven) consentState.value = 'upgrade'
      else consentState.value = 'accepted'
    } catch (error) {
      console.error('Voice AI readiness check failed', error)
      missing.push('not-enabled')
    } finally {
      blockers.value = missing
      checking.value = false
    }
  }

  function hydrateFromSavedRows() {
    const row = (state.voiceAiNotifications || []).find(entry => entry.receiverType === ReceiverType.CONTACT)
    if (!row) return
    if (row._id) rowId.value = row._id
    enabled.value = row.isActive && !row.deleted
    form.agentId = row.voiceAgentId || ''
    form.fromNumber = row.fromNumber || ''
    const saved = row.beforeTime?.[0]
    if (saved) form.interval = { timeOffset: Number(saved.timeOffset) || 0, unit: saved.unit }
  }

  /** The reminder call goes to the contact on the appointment only, so one row carries the whole configuration. */
  function buildRow(): NotificationConfig {
    return {
      _id: rowId.value,
      altType: AltType.CALENDAR,
      altId: props.calendarId,
      channel: NotificationChannel.VOICE_AI,
      notificationType: NotificationType.REMINDER,
      receiverType: ReceiverType.CONTACT,
      templateId: '',
      body: '',
      subject: '',
      voiceAgentId: form.agentId,
      fromNumber: form.fromNumber,
      beforeTime: [{ timeOffset: Number(form.interval.timeOffset), unit: form.interval.unit }],
      afterTime: [],
      selectedUsers: [],
      isActive: enabled.value,
      deleted: !enabled.value,
    }
  }

  /** Resolves with an error message when the settings can't be saved as they are. */
  function validateAll(): string | null {
    if (!enabled.value) return null
    if (!form.agentId) return t('eventNotification.voiceAi.agentRequired')
    if (!form.fromNumber) return t('eventNotification.voiceAi.fromNumberRequired')
    if (offsetErrorMessage.value) return offsetErrorMessage.value
    if (consentState.value === 'required' && !form.termsAgreed) return t('eventNotification.voiceAi.consent.required')
    return null
  }

  // Unlike the other tabs, switching Voice AI on does not save immediately: an enabled channel with
  // no agent or number would be persisted as a reminder that can never place a call.
  async function save(): Promise<boolean> {
    const row = buildRow()
    try {
      // Automated calls need the account's acceptance of the calling terms on record first.
      if (enabled.value && consentState.value !== 'accepted' && form.termsAgreed) {
        await VoiceAiReminderService.acceptOutboundTerms(AppState.locationId)
        consentState.value = 'accepted'
      }
      if (row._id && !isVirtualNotification(row._id)) {
        await EventNotificationService.updateEventNotifications([row])
      } else {
        const { data } = await EventNotificationService.createEventNotifications([stripNotificationIdForCreate(row)])
        const created = Array.isArray(data) ? data[0] : data
        if (created?._id) rowId.value = created._id
      }
    } catch (error) {
      console.error('Voice AI notification save failed', error)
      return false
    }
    emit('update-notification', {
      channel: NotificationChannel.VOICE_AI,
      type: NotificationType.REMINDER,
      status: enabled.value,
    })
    return true
  }

  defineExpose({ validateAll, save })

  onMounted(async () => {
    hydrateFromSavedRows()
    await checkReadiness()
  })
</script>

<style scoped>
  .disabled-div {
    position: relative;
    pointer-events: none;
    user-select: none;
    opacity: 0.5;
  }
</style>
